// src/hooks/useSupabase.js
// Custom hooks for Supabase data operations

import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, uploadFile as uploadFileToStorage, deleteFileFromStorage, getFileUrl, geocodeAddress } from '../lib/supabase';
import { titleCaseLocation } from '../lib/textFormat';

// Hook for authentication state
export function useAuth() {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [organization, setOrganization] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // MFA state. 'idle' = not signed in or not yet checked. 'satisfied' = user
  // is OAuth (IdP handles MFA) or already verified to aal2. 'enrollment_required'
  // = email/password user with no TOTP factor — must enroll. 'challenge_required'
  // = email/password user with verified factor on aal1 — must enter TOTP code.
  const [mfaState, setMfaState] = useState('idle');
  const [mfaFactors, setMfaFactors] = useState([]);
  const userProfileRef = useRef(null);
  // Pre-auth org config — drives the login screen (which sign-in method
  // card(s) to show) and whether to show the first-run bootstrap wizard
  // instead of a normal login screen. Fetched independently of session
  // state via a SECURITY DEFINER RPC, since an anonymous or profile-less
  // caller can't read the organizations table directly (RLS-scoped to
  // "your own org", which doesn't exist yet for either case).
  const [orgConfigLoading, setOrgConfigLoading] = useState(true);
  const [orgExists, setOrgExists] = useState(true); // optimistic default avoids a bootstrap-wizard flash for the common case
  const [ssoEnabled, setSsoEnabled] = useState(true);
  const [passwordAuthEnabled, setPasswordAuthEnabled] = useState(true);
  const [publicOrgBranding, setPublicOrgBranding] = useState(null);

  const fetchOrgPublicConfig = useCallback(async () => {
    try {
      const { data, error } = await supabase.rpc('get_org_public_config');
      if (error) throw error;
      setOrgExists(!!data?.org_exists);
      setSsoEnabled(data?.sso_enabled ?? true);
      setPasswordAuthEnabled(data?.password_auth_enabled ?? true);
      setPublicOrgBranding(data?.org_exists ? {
        name: data.name,
        logo_url: data.logo_url,
        primary_color: data.primary_color,
        secondary_color: data.secondary_color,
      } : null);
    } catch (err) {
      console.error('Error fetching org public config:', err);
      // Fail open to "org exists, both methods available" rather than
      // stranding every user on a bootstrap wizard because of a transient
      // network error.
      setOrgExists(true);
    } finally {
      setOrgConfigLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrgPublicConfig();
  }, [fetchOrgPublicConfig]);

  // Recompute MFA gate after auth events. Called on every SIGNED_IN /
  // MFA_CHALLENGE_VERIFIED, and when the user enrolls a factor.
  const refreshMfaState = useCallback(async () => {
    try {
      const { data: { user: u } } = await supabase.auth.getUser();
      if (!u) { setMfaState('idle'); setMfaFactors([]); return; }
      // SSO users (Microsoft etc.) have MFA enforced at the IdP — skip our gate.
      // Anyone with ANY non-email provider linked (azure, google, etc.) counts
      // as an SSO user. This intentionally exempts users who originally
      // created a password account and later linked M365 — providers will be
      // ['email', 'azure'] for them, but they're LFG staff who get MFA via
      // Microsoft. Only pure email-only accounts (no SSO ever linked) hit the
      // TOTP gate; those are the seller/broker/accountant external users.
      const providers = u.app_metadata?.providers || [];
      const hasOAuthProvider = providers.some(p => p && p !== 'email');
      if (hasOAuthProvider) { setMfaState('satisfied'); setMfaFactors([]); return; }

      // deal_advisor accounts used to be exempted here — they were
      // magic-link-only ("no password or sign-up needed"), so there was
      // never an enrollment step to point them at. Now that they're
      // password-based like every other external role (they can span
      // multiple deals' worth of financials/documents via deal_access,
      // which is exactly the population that most needs MFA), they go
      // through the same TOTP gate + grace period as everyone else below.
      const { data: profile, error: profileErr } = await supabase
        .from('users')
        .select('role, mfa_grace_until')
        .eq('id', u.id)
        .maybeSingle();
      if (profileErr) {
        // Was silently swallowed before (destructured without checking
        // error) — if this query ever fails for a real session, it fell
        // through to the TOTP gate with no trace of why. Log it loudly so
        // a repeat of "deal_advisor still hits MFA" is actually diagnosable
        // instead of re-verifying the DB state from scratch every time.
        console.error('refreshMfaState: profile lookup failed', profileErr, 'for user', u.id);
      }
      console.log('refreshMfaState check:', { userId: u.id, providers, profileRole: profile?.role, mfaGraceUntil: profile?.mfa_grace_until });

      const { data: factorsData } = await supabase.auth.mfa.listFactors();
      const verified = (factorsData?.totp || []).filter(f => f.status === 'verified');
      setMfaFactors(verified);
      if (verified.length === 0) {
        // Honor a per-user grace period if set. mfa_grace_until is set by
        // default to created_at + 7 days for new accounts; admins can edit.
        try {
          const grace = profile?.mfa_grace_until ? new Date(profile.mfa_grace_until) : null;
          if (grace && grace > new Date()) {
            setMfaState('satisfied');
            return;
          }
        } catch (e) {
          console.warn('mfa_grace_until lookup failed:', e?.message);
        }
        setMfaState('enrollment_required');
        return;
      }
      const { data: aal } = await supabase.auth.mfa.getAuthenticatorAssuranceLevel();
      if (aal?.currentLevel === 'aal1' && aal?.nextLevel === 'aal2') {
        setMfaState('challenge_required');
      } else {
        setMfaState('satisfied');
      }
    } catch (e) {
      console.error('refreshMfaState failed:', e);
      // Fail open during error so we don't lock people out — but log loudly.
      setMfaState('satisfied');
    }
  }, []);

  // Keep ref in sync with state
  useEffect(() => {
    userProfileRef.current = userProfile;
  }, [userProfile]);

  // Fetch user profile helper. Auto-provisions a public.users row for
  // first-time SSO sign-ins where Supabase's OAuth flow creates the
  // auth.users record but no app-side profile exists yet. Without this,
  // queries that depend on userProfile / organization_id silently
  // return null and downstream useEffects either spin forever or
  // 500-loop until the browser kills the tab. Hoisted out of the auth
  // effect (as a stable useCallback) so it can also be called directly
  // after the first-run bootstrap wizard creates a brand new profile.
  const fetchUserProfile = useCallback(async (userId) => {
    try {
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('*')
        .eq('id', userId)
        .maybeSingle();

      if (userError) {
        console.error('Error fetching user profile:', userError);
        return { userData: null, orgData: null };
      }

      let row = userData;

      // No profile row — try to provision one. Only works for users whose
      // email domain is allowed to auto-provision for this org (see
      // organizations.allowed_email_domains — configurable per deployment,
      // not hardcoded). External users must come through the team_invites
      // flow which creates the profile via signUp().
      if (!row) {
        const { data: { user: authUser } } = await supabase.auth.getUser();
        const email = (authUser?.email || '').toLowerCase();
        const { data: isAllowedDomain } = email
          ? await supabase.rpc('is_allowed_email_domain', { email })
          : { data: false };
        if (authUser && isAllowedDomain) {
          // Default to the single org in the DB (single-tenant deployment).
          const { data: orgRows } = await supabase
            .from('organizations').select('id').limit(1);
          const defaultOrgId = orgRows?.[0]?.id || null;
          const fullName = authUser.user_metadata?.full_name
            || authUser.user_metadata?.name
            || email.split('@')[0];
          const { data: created, error: insertErr } = await supabase
            .from('users')
            .insert({
              id: userId,
              email,
              full_name: fullName,
              role: 'member',
              organization_id: defaultOrgId,
            })
            .select()
            .single();
          if (insertErr) {
            console.error('Failed to auto-provision profile:', insertErr);
          } else {
            row = created;
          }
        }
      }

      let orgData = null;
      if (row?.organization_id) {
        const { data } = await supabase
          .from('organizations')
          .select('*')
          .eq('id', row.organization_id)
          .single();
        orgData = data;
      }

      return { userData: row, orgData };
    } catch (err) {
      console.error('Error in fetchUserProfile:', err);
      return { userData: null, orgData: null };
    }
  }, []);

  useEffect(() => {
    let mounted = true;
    let initialized = false;

    const init = async () => {
      try {
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (!mounted) return;
        
        if (sessionError) {
          console.error('Session error:', sessionError);
          setError(sessionError.message);
          setLoading(false);
          return;
        }
        
        if (session?.user) {
          setUser(session.user);
          const { userData, orgData } = await fetchUserProfile(session.user.id);

          if (mounted) {
            setUserProfile(userData);
            setOrganization(orgData);
            // Recompute MFA gate on hard refresh so a refresh doesn't bypass MFA
            refreshMfaState();
          }
        }
      } catch (err) {
        if (err.name === 'AbortError' || err.message?.includes('abort')) {
          return;
        }
        console.error('Auth init error:', err);
        if (mounted) setError(err.message);
      } finally {
        if (mounted) {
          setLoading(false);
          initialized = true;
        }
      }
    };

    init();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        if (!mounted) return;
        
        console.log('Auth event:', event);
        
        // Handle password recovery event
        if (event === 'PASSWORD_RECOVERY') {
          console.log('Password recovery event detected');
          setUser(session?.user || null);
          setLoading(false);
          // Set a flag that App.jsx can check
          window.__passwordRecoveryMode = true;
          return;
        }
        
        // Skip events during initial load
        if (!initialized && event !== 'SIGNED_OUT') {
          return;
        }
        
        if (event === 'SIGNED_OUT') {
          setUser(null);
          setUserProfile(null);
          setOrganization(null);
          setError(null);
          setMfaState('idle');
          setMfaFactors([]);
          setLoading(false);
          return;
        }

        if (event === 'MFA_CHALLENGE_VERIFIED' || event === 'TOKEN_REFRESHED') {
          refreshMfaState();
        }

        if (event === 'SIGNED_IN' && session?.user) {
          // Recompute MFA gate (cheap; just checks factors + AAL).
          refreshMfaState();
          // Skip if we already have this user's profile loaded
          if (userProfileRef.current?.id === session.user.id) {
            setLoading(false);
            return;
          }
          
          setLoading(true);
          setUser(session.user);
          
          try {
            let { userData, orgData } = await fetchUserProfile(session.user.id);
            
            // If profile not found, wait a moment and retry (might still be creating)
            if (!userData) {
              console.log('Profile not found, retrying in 500ms...');
              await new Promise(resolve => setTimeout(resolve, 500));
              const retryResult = await fetchUserProfile(session.user.id);
              userData = retryResult.userData;
              orgData = retryResult.orgData;
            }
            
            // Still no profile? Try one more time
            if (!userData) {
              console.log('Profile still not found, retrying in 1000ms...');
              await new Promise(resolve => setTimeout(resolve, 1000));
              const retryResult = await fetchUserProfile(session.user.id);
              userData = retryResult.userData;
              orgData = retryResult.orgData;
            }
            
            if (mounted) {
              setUserProfile(userData);
              setOrganization(orgData);
            }
          } catch (err) {
            if (err.name !== 'AbortError' && !err.message?.includes('abort')) {
              console.error('Error fetching profile on sign in:', err);
            }
          } finally {
            if (mounted) setLoading(false);
          }
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    setError(null);
    setLoading(true);
    
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password
      });
      
      if (error) {
        setLoading(false);
        return { data: null, error };
      }
      
      // The onAuthStateChange will handle fetching the profile
      return { data, error: null };
    } catch (err) {
      setLoading(false);
      return { data: null, error: err };
    }
  };

  const signUp = async (email, password, fullName) => {
    setError(null);
    setLoading(true);
    
    console.log('Starting signup for:', email);
    
    try {
      // Check if there's a pending invite for this email
      const { data: inviteData, error: inviteError } = await supabase
        .from('team_invites')
        .select('*')
        .eq('email', email.toLowerCase())
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      
      console.log('Invite lookup result:', { inviteData, inviteError });
      
      if (inviteError || !inviteData) {
        setLoading(false);
        return { error: { message: 'No pending invitation found for this email. Please contact your administrator.' } };
      }

      // If invite already accepted, the user already has an account — sign them in instead
      if (inviteData.accepted_at) {
        console.log('Invite already accepted, attempting sign in...');
        const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
          email, password
        });
        if (signInError) {
          setLoading(false);
          return { error: { message: 'Your account has already been created. Please sign in with your email and password.' } };
        }
        setLoading(false);
        return { data: signInData };
      }
      
      // Try to create auth user
      console.log('Creating auth user...');
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            full_name: fullName
          },
          emailRedirectTo: window.location.origin
        }
      });
      
      console.log('Auth signup result:', { authData, authError });
      
      // 422 from Supabase auth.signUp has multiple possible meanings.
      // Surface the real one to the user instead of assuming any single
      // cause.
      if (authError) {
        const rawMsg = (authError.message || '').toLowerCase();
        const status = authError.status;
        console.warn('Signup error details:', { status, message: authError.message });

        // Weak password
        if (rawMsg.includes('weak') || rawMsg.includes('password should')) {
          setLoading(false);
          return { error: { message: 'Password is too weak. Use at least 8 characters with uppercase, lowercase, and a number.' } };
        }

        // Rate limited
        if (rawMsg.includes('rate limit') || rawMsg.includes('too many') || status === 429) {
          setLoading(false);
          return { error: { message: 'Too many sign-up attempts in a short window. Wait a few minutes and try again, or ask your administrator to resend the invite.' } };
        }

        // Signups disabled
        if (rawMsg.includes('signups not allowed') || rawMsg.includes('signup is disabled') || rawMsg.includes('signups are disabled')) {
          setLoading(false);
          return { error: { message: 'Sign-up is currently disabled on this project. Ask your administrator to enable email signups in Supabase Auth settings.' } };
        }

        // Existing user — but ONLY if the message confirms it. Don't
        // assume every 422 is "already exists" because that masks the
        // actual reason for failure.
        const isExistingUser = rawMsg.includes('already registered')
          || rawMsg.includes('user already exists')
          || rawMsg.includes('already been registered');
        if (isExistingUser) {
          console.log('User already exists in Auth, trying to sign in and update profile...');
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email, password,
          });
          if (signInError) {
            try {
              await supabase.auth.resetPasswordForEmail(email, { redirectTo: window.location.origin });
            } catch (e) { console.warn('Auto password reset failed:', e?.message); }
            setLoading(false);
            return {
              error: {
                code: 'EXISTING_USER_RESET_SENT',
                message: `An account already exists for ${email}. We just sent a password-reset link to that inbox — open it to finish setting your password, then sign in.`,
              },
            };
          }
          if (signInData?.user) {
            const { error: profileError } = await supabase
              .from('users')
              .upsert({
                id: signInData.user.id,
                email: email.toLowerCase(),
                full_name: fullName,
                organization_id: inviteData.organization_id,
                role: inviteData.role || 'member'
              }, { onConflict: 'id' });
            if (profileError) console.error('Profile update error:', profileError);
            await supabase
              .from('team_invites')
              .update({ accepted_at: new Date().toISOString() })
              .eq('id', inviteData.id);
            setLoading(false);
            return { data: signInData };
          }
        }

        // Fallback: surface the raw Supabase error so the user sees
        // what's actually happening instead of a generic "looped" state.
        setLoading(false);
        return {
          error: {
            message: `Sign-up failed: ${authError.message || `HTTP ${status}`}. Try again, ask your administrator to resend the invite, or use the password-reset link from your email if you already have an account.`,
          },
        };
      }

      // If user was created (might already exist)
      if (authData?.user) {
        console.log('Creating user profile for:', authData.user.id);
        
        // Create user profile linked to the invite's organization
        const { data: profileData, error: profileError } = await supabase
          .from('users')
          .upsert({
            id: authData.user.id,
            email: email.toLowerCase(),
            full_name: fullName,
            organization_id: inviteData.organization_id,
            role: inviteData.role || 'member'
          }, { onConflict: 'id' })
          .select()
          .single();
        
        console.log('Profile creation result:', { profileData, profileError });
          
        if (profileError) {
          console.error('Profile creation error:', profileError);
        }
        
        // Mark invite as accepted
        const { error: updateError } = await supabase
          .from('team_invites')
          .update({ accepted_at: new Date().toISOString() })
          .eq('id', inviteData.id);
        
        console.log('Invite update result:', { updateError });
          
        if (updateError) {
          console.error('Failed to mark invite as accepted:', updateError);
        }
      }
      
      // Sign out after signup - force user to login manually for clean state
      console.log('Signing out to force manual login...');
      await supabase.auth.signOut();
      
      // Clear local state immediately
      setUser(null);
      setUserProfile(null);
      setOrganization(null);
      
      setLoading(false);
      
      // Always return success with forceLogin flag
      return { 
        data: authData, 
        forceLogin: true,
        message: 'Account created successfully! Please sign in.'
      };
    } catch (err) {
      console.error('Signup error:', err);
      setLoading(false);
      return { error: err };
    }
  };

  // Sign-up for the first-run bootstrap wizard only. Unlike signUp() above,
  // this does NOT require a pending team_invites row (there can't be one —
  // nobody exists yet to have sent it) and does NOT force a sign-out
  // afterward — the wizard needs the resulting session to stay live so it
  // can immediately call bootstrapOrganization() while auth.uid() resolves.
  const bootstrapSignUp = async (email, password, fullName) => {
    setError(null);
    setLoading(true);
    try {
      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: { data: { full_name: fullName } },
      });
      setLoading(false);
      if (error) return { error };
      // If email confirmation is required on this project, signUp() won't
      // return a live session — the caller needs to tell the user to check
      // their inbox rather than proceed straight to bootstrapOrganization.
      return { data, needsEmailConfirmation: !data?.session };
    } catch (err) {
      setLoading(false);
      return { error: err };
    }
  };

  // Creates the organization + seeds default categories/templates + makes
  // the calling (already-authenticated) user its first admin. Works after
  // either bootstrapSignUp() or signInWithMicrosoft() — both leave the
  // caller authenticated, which is all this RPC needs.
  const bootstrapOrganization = async ({ orgName, adminName, ssoEnabled: sso, passwordAuthEnabled: pwd, allowedEmailDomains }) => {
    try {
      const { data, error } = await supabase.rpc('bootstrap_organization', {
        org_name: orgName,
        admin_name: adminName || null,
        p_sso_enabled: sso ?? true,
        p_password_auth_enabled: pwd ?? true,
        p_allowed_email_domains: allowedEmailDomains || null,
      });
      if (error) return { error };
      if (!data?.success) return { error: { message: data?.error || 'Could not create organization' } };
      // Pull the freshly-created profile/org into state so the app
      // transitions straight into the dashboard as the new admin, no
      // forced re-login.
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (authUser) {
        setUser(authUser);
        const { userData, orgData } = await fetchUserProfile(authUser.id);
        setUserProfile(userData);
        setOrganization(orgData);
        refreshMfaState();
      }
      await fetchOrgPublicConfig();
      return { data };
    } catch (err) {
      return { error: err };
    }
  };

  const resetPassword = async (email) => {
    try {
      // Custom flow via the request-password-reset edge function which
      // generates a recovery link with auth.admin.generateLink and sends
      // a branded email through Resend. Bypasses Supabase's built-in SMTP
      // (which isn't configured for this project and was returning 500).
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/request-password-reset`;
      const r = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          email,
          redirectTo: `${window.location.origin}?reset=true`,
        }),
      });
      const body = await r.json().catch(() => ({}));
      if (!r.ok) {
        return { error: { message: body?.error || `HTTP ${r.status}` } };
      }
      return { data: true };
    } catch (err) {
      return { error: err };
    }
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
      setUser(null);
      setUserProfile(null);
      setOrganization(null);
      setError(null);
    } catch (err) {
      console.error('Sign out error:', err);
    }
  };

  // Sign in with Microsoft 365
  const signInWithMicrosoft = async () => {
    try {
      setError(null);
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'azure',
        options: {
          scopes: 'email profile openid',
          redirectTo: window.location.origin,
        }
      });

      if (error) throw error;
      return { data, error: null };
    } catch (err) {
      console.error('Microsoft sign-in error:', err);
      setError(err.message);
      return { data: null, error: err };
    }
  };

  // Session timeout - 30 minutes of inactivity
  useEffect(() => {
    if (!user) return;
    
    const TIMEOUT_MS = 30 * 60 * 1000; // 30 minutes
    let timeoutId;
    
    const resetTimer = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        console.log('Session timeout - signing out due to inactivity');
        signOut();
      }, TIMEOUT_MS);
    };
    
    // Events that reset the timer
    const events = ['mousedown', 'keydown', 'scroll', 'touchstart', 'mousemove'];
    
    events.forEach(event => {
      window.addEventListener(event, resetTimer, { passive: true });
    });
    
    // Start the timer
    resetTimer();
    
    return () => {
      clearTimeout(timeoutId);
      events.forEach(event => {
        window.removeEventListener(event, resetTimer);
      });
    };
  }, [user]);

  return {
    user,
    userProfile,
    organization,
    loading,
    error,
    signIn,
    signUp,
    signOut,
    signInWithMicrosoft,
    resetPassword,
    bootstrapSignUp,
    bootstrapOrganization,
    // Pre-auth org config — drives the login screen and bootstrap-wizard gate
    orgConfigLoading,
    orgExists,
    ssoEnabled,
    passwordAuthEnabled,
    publicOrgBranding,
    isAdmin: userProfile?.role === 'admin',
    isMember: userProfile?.role === 'member',
    // isViewer covers anyone with read-only or restricted-write access who
    // shouldn't get the full editing affordance — integration_viewer can edit
    // their domain (tasks, vendors, BI) so they're NOT in this set.
    isViewer: userProfile?.role === 'viewer' || userProfile?.role === 'limited_viewer',
    isLimitedViewer: userProfile?.role === 'limited_viewer',
    isDealAdvisor: userProfile?.role === 'deal_advisor',
    // integration = post-close integration staff. Same writes as
    // member on operational data, but financial info / legal docs / NDAs
    // / asking price / EBITDA / Finance + Market + Prospects views are all
    // hidden in the UI.
    isIntegration: userProfile?.role === 'integration',
    // board_member = full read access to everything (including admin views,
    // financials, all deals). Write access ONLY on deals they personally
    // created (deals.created_by = their user id). DealView shadows isViewer
    // to true on deals they didn't create. AdminView renders with a
    // read-only wrapper that disables interactive controls.
    isBoardMember: userProfile?.role === 'board_member',
    // MFA gate — App.jsx renders enrollment / challenge screens based on these
    mfaState,
    mfaFactors,
    refreshMfaState,
  };
}

// Hook for deals
export function useDeals(organizationId, userInfo) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const hasLoadedOnce = useRef(false);

  const fetchDeals = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }
    
    try {
      // Only show loading spinner on first load, not refreshes
      if (!hasLoadedOnce.current) {
        setLoading(true);
      }
      
      // For deal_advisors, get their allowed deal IDs first
      let allowedDealIds = null;
      if (userInfo?.role === 'deal_advisor' && userInfo?.id) {
        const { data: accessData } = await supabase
          .from('deal_access')
          .select('deal_id, notify_on_upload')
          .eq('user_id', userInfo.id);
        allowedDealIds = (accessData || []).map(a => a.deal_id);
        var notifyByDealId = {};
        (accessData || []).forEach(a => { notifyByDealId[a.deal_id] = a.notify_on_upload; });
        
        if (allowedDealIds.length === 0) {
          setDeals([]);
          hasLoadedOnce.current = true;
          setLoading(false);
          return;
        }
      }
      
      // Try the view first, fall back to direct table query
      let data, error;
      
      try {
        const result = await supabase
          .from('deal_summary')
          .select('*')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });
        
        data = result.data;
        error = result.error;
      } catch (viewError) {
        // View might not exist, try direct query
        console.warn('deal_summary view not available, using direct query');
        const result = await supabase
          .from('deals')
          .select('*')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });
        
        data = result.data?.map(d => ({
          ...d,
          total_tasks: 0,
          completed_tasks: 0,
          requested_tasks: 0,
          total_files: 0
        }));
        error = result.error;
      }

      if (error) throw error;
      
      // Filter for deal_advisors
      let filteredData = data || [];
      if (allowedDealIds) {
        filteredData = filteredData
          .filter(d => allowedDealIds.includes(d.id))
          .map(d => ({ ...d, notify_on_upload: notifyByDealId?.[d.id] || false }));
      }
      
      setDeals(filteredData);
      hasLoadedOnce.current = true;
    } catch (err) {
      console.error('Error fetching deals:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [organizationId, userInfo?.id, userInfo?.role]);

  useEffect(() => {
    fetchDeals();
    
    if (!organizationId) return;
    
    // Subscribe to realtime changes
    const channel = supabase
      .channel('deals-changes')
      .on('postgres_changes', 
        { event: '*', schema: 'public', table: 'deals', filter: `organization_id=eq.${organizationId}` },
        () => fetchDeals()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [organizationId, fetchDeals]);

  const createDeal = async (name) => {
    const { data, error } = await supabase
      .from('deals')
      .insert({
        organization_id: organizationId,
        name
      })
      .select()
      .single();

    if (error) throw error;
    await fetchDeals();
    return data;
  };

  const updateDeal = async (dealId, updates) => {
    const { error } = await supabase
      .from('deals')
      .update(updates)
      .eq('id', dealId);

    if (error) throw error;
    await fetchDeals();
  };

  const deleteDeal = async (dealId) => {
    // Soft delete - set deleted_at timestamp instead of actually deleting
    const { error } = await supabase
      .from('deals')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', dealId);

    if (error) throw error;
    await fetchDeals();
  };

  // deal_advisor self-service: opt in/out of upload notifications for one
  // of their assigned deals. Writes via a SECURITY DEFINER RPC scoped to
  // auth.uid() rather than a direct deal_access update, since deal_access
  // grant/management is otherwise admin-only.
  const setNotifyOnUpload = async (dealId, notify) => {
    const { error } = await supabase.rpc('set_own_deal_notification_pref', { p_deal_id: dealId, p_notify: notify });
    if (error) throw error;
    setDeals(prev => prev.map(d => d.id === dealId ? { ...d, notify_on_upload: notify } : d));
  };

  return {
    deals,
    loading,
    error,
    createDeal,
    updateDeal,
    deleteDeal,
    setNotifyOnUpload,
    refresh: fetchDeals
  };
}

// Hook for deleted deals (trash)
export function useDeletedDeals(organizationId) {
  const [deletedDeals, setDeletedDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDeletedDeals = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('deals')
        .select('id, name, deleted_at, stage, created_at')
        .eq('organization_id', organizationId)
        .not('deleted_at', 'is', null)
        .order('deleted_at', { ascending: false });

      if (error) throw error;
      setDeletedDeals(data || []);
    } catch (err) {
      console.error('Error fetching deleted deals:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchDeletedDeals();
  }, [organizationId, fetchDeletedDeals]);

  const restoreDeal = async (dealId) => {
    const { error } = await supabase
      .from('deals')
      .update({ deleted_at: null })
      .eq('id', dealId);

    if (error) throw error;
    await fetchDeletedDeals();
  };

  const permanentlyDeleteDeal = async (dealId) => {
    // First delete all related data
    await supabase.from('files').delete().eq('deal_id', dealId);
    await supabase.from('tasks').delete().eq('deal_id', dealId);
    
    const { error } = await supabase
      .from('deals')
      .delete()
      .eq('id', dealId);

    if (error) throw error;
    await fetchDeletedDeals();
  };

  return {
    deletedDeals,
    loading,
    restoreDeal,
    permanentlyDeleteDeal,
    refresh: fetchDeletedDeals
  };
}

// Hook for single deal with tasks
export function useDeal(dealId) {
  const [deal, setDeal] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const mountedRef = useRef(true);
  const hasFetchedRef = useRef(false);

  const fetchDeal = useCallback(async () => {
    if (!dealId) {
      setLoading(false);
      return;
    }

    try {
      // Only set loading on the very first fetch. Subsequent refetches
      // (from realtime updates) silently swap the data — preserving any
      // input focus the user has on a form field.
      if (mountedRef.current && !hasFetchedRef.current) setLoading(true);

      // Fetch deal
      const { data: dealData, error: dealError } = await supabase
        .from('deals')
        .select('*')
        .eq('id', dealId)
        .single();

      if (dealError) throw dealError;
      if (mountedRef.current) setDeal(dealData);
      hasFetchedRef.current = true;

      // Fetch tasks with files
      const { data: tasksData, error: tasksError } = await supabase
        .from('tasks')
        .select(`
          *,
          files (*)
        `)
        .eq('deal_id', dealId)
        .order('sort_order', { ascending: true });

      if (tasksError) throw tasksError;
      if (mountedRef.current) setTasks(tasksData || []);
    } catch (err) {
      console.error('Error fetching deal:', err);
      if (mountedRef.current) setError(err.message);
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    mountedRef.current = true;
    // New deal id → restart loading state
    hasFetchedRef.current = false;
    fetchDeal();

    if (!dealId) return;

    // Debounce realtime updates to prevent refresh loops
    let debounceTimer = null;
    const debouncedFetch = () => {
      if (debounceTimer) clearTimeout(debounceTimer);
      debounceTimer = setTimeout(() => {
        if (mountedRef.current) fetchDeal();
      }, 500); // Wait 500ms after last change before refreshing
    };

    // Subscribe to realtime changes
    const dealsChannel = supabase
      .channel(`deal-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'deals', filter: `id=eq.${dealId}` },
        () => debouncedFetch()
      )
      .subscribe();

    const tasksChannel = supabase
      .channel(`tasks-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'tasks', filter: `deal_id=eq.${dealId}` },
        () => debouncedFetch()
      )
      .subscribe();

    const filesChannel = supabase
      .channel(`files-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'files', filter: `deal_id=eq.${dealId}` },
        () => debouncedFetch()
      )
      .subscribe();

    return () => {
      mountedRef.current = false;
      if (debounceTimer) clearTimeout(debounceTimer);
      supabase.removeChannel(dealsChannel);
      supabase.removeChannel(tasksChannel);
      supabase.removeChannel(filesChannel);
    };
  }, [dealId, fetchDeal]);

  const updateDeal = async (updates) => {
    // Optimistic local update first — keeps form focus intact while we
    // wait for the round-trip. The realtime channel will reconcile any
    // server-side drift, but for our own edits the local state is the
    // source of truth.
    if (mountedRef.current) {
      setDeal(prev => prev ? { ...prev, ...updates, updated_at: new Date().toISOString() } : prev);
    }
    const { error } = await supabase
      .from('deals')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', dealId);

    if (error) throw error;
    // No explicit refetch here. The dealsChannel realtime subscription
    // picks up the update (debounced 500ms) and only refetches if there's
    // server-side drift. Avoiding the immediate refetch keeps the user's
    // tab focus on the next field instead of nuking it on every blur.
  };

  const updateTask = async (taskId, updates) => {
    const { error } = await supabase
      .from('tasks')
      .update({
        ...updates,
        updated_at: new Date().toISOString()
      })
      .eq('id', taskId);

    if (error) throw error;
    
    // Immediately refetch to update UI
    await fetchDeal();
  };

  const addTask = async (taskData) => {
    const { data, error } = await supabase
      .from('tasks')
      .insert({
        deal_id: dealId,
        organization_id: deal.organization_id,
        is_custom: true,
        ...taskData
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  };

  const deleteTask = async (taskId) => {
    // First delete associated files from storage
    const task = tasks.find(t => t.id === taskId);
    if (task?.files) {
      for (const file of task.files) {
        try {
          await deleteFileFromStorage(file.storage_path);
        } catch (err) {
          console.error('Error deleting file from storage:', err);
        }
      }
    }

    const { error } = await supabase
      .from('tasks')
      .delete()
      .eq('id', taskId);

    if (error) throw error;
  };

  const uploadFile = async (taskId, file, uploadedBy) => {
    const fileData = await uploadFileToStorage(file, dealId, taskId, deal.organization_id);
    
    const { data, error } = await supabase
      .from('files')
      .insert({
        task_id: taskId,
        deal_id: dealId,
        organization_id: deal.organization_id,
        name: fileData.name,
        original_name: fileData.originalName,
        storage_path: fileData.path,
        size_bytes: fileData.size,
        mime_type: fileData.mimeType,
        uploaded_by_id: uploadedBy?.id || null,
        uploaded_by_name: uploadedBy?.name || 'Unknown',
        uploaded_by_type: uploadedBy?.type || 'user'
      })
      .select()
      .single();

    if (error) throw error;

    // Update task status if it was "Requested"
    const task = tasks.find(t => t.id === taskId);
    if (task?.status === 'Requested') {
      await updateTask(taskId, { status: 'Received' });
    }

    // The DB trigger `on_file_upload` (notify_file_upload) creates the
    // upload_notifications row when uploaded_by_type='seller', gated on
    // the org's digest_settings.doc_notifications_enabled. No client-side
    // insert needed — doing both produced duplicate alerts.

    return data;
  };

  const deleteFile = async (taskId, fileId, storagePath) => {
    // Delete from storage
    await deleteFileFromStorage(storagePath);
    
    // Delete from database
    const { error } = await supabase
      .from('files')
      .delete()
      .eq('id', fileId);

    if (error) throw error;
  };

  const applyDueDates = async (overrideDates = {}) => {
    // Use provided dates or fall back to deal dates
    const loiDate = overrideDates.loi_date || deal?.loi_date;
    const closeDate = overrideDates.target_close_date || deal?.target_close_date;
    const createdDate = overrideDates.created_date || deal?.created_date;
    
    if (!loiDate && !closeDate) {
      throw new Error('Set LOI Date or Target Close Date first');
    }
    
    // Calculate due dates for all tasks based on their relative_days and base_date
    const updates = tasks.map(task => {
      let baseDate = null;
      
      if (task.base_date === 'loi' && loiDate) {
        baseDate = new Date(loiDate);
      } else if (task.base_date === 'close' && closeDate) {
        baseDate = new Date(closeDate);
      } else if (task.base_date === 'created' && createdDate) {
        baseDate = new Date(createdDate);
      } else if (loiDate) {
        // Default to LOI if available
        baseDate = new Date(loiDate);
      } else if (closeDate) {
        // Fall back to close date
        baseDate = new Date(closeDate);
      }
      
      // Check for null/undefined relative_days, but allow 0
      if (!baseDate || task.relative_days === null || task.relative_days === undefined) {
        return null;
      }
      
      const dueDate = new Date(baseDate);
      dueDate.setDate(dueDate.getDate() + task.relative_days);
      
      return {
        id: task.id,
        due_date: dueDate.toISOString().split('T')[0]
      };
    }).filter(Boolean);
    
    console.log('Applying due dates to tasks:', updates);
    
    // Update all tasks in parallel for speed
    await Promise.all(updates.map(update => 
      supabase
        .from('tasks')
        .update({ due_date: update.due_date })
        .eq('id', update.id)
    ));

    // Also apply to deal_info_requests if it has relative_days + base_date set
    const { data: infoReq } = await supabase
      .from('deal_info_requests')
      .select('id, relative_days, base_date')
      .eq('deal_id', dealId)
      .maybeSingle();

    if (infoReq && infoReq.relative_days !== null && infoReq.relative_days !== undefined) {
      let infoBase = null;
      if (infoReq.base_date === 'loi' && loiDate)        infoBase = new Date(loiDate);
      else if (infoReq.base_date === 'close' && closeDate) infoBase = new Date(closeDate);
      else if (infoReq.base_date === 'created' && createdDate) infoBase = new Date(createdDate);
      else if (loiDate)   infoBase = new Date(loiDate);
      else if (closeDate) infoBase = new Date(closeDate);

      if (infoBase) {
        const d = new Date(infoBase);
        d.setDate(d.getDate() + infoReq.relative_days);
        await supabase
          .from('deal_info_requests')
          .update({ due_date: d.toISOString().split('T')[0] })
          .eq('id', infoReq.id);
      }
    }

    await fetchDeal();
  };

  const markTasksRequested = async (taskIds) => {
    const { error } = await supabase
      .from('tasks')
      .update({ status: 'Requested' })
      .in('id', taskIds);

    if (error) throw error;

    // Log request history
    await supabase
      .from('request_history')
      .insert({
        deal_id: dealId,
        organization_id: deal.organization_id,
        task_ids: taskIds,
        sent_to_email: deal.primary_email
      });
    
    await fetchDeal();
  };

  const bulkUpdateTasks = async (taskIds, updates) => {
    if (!taskIds || taskIds.length === 0) return;
    
    // Ensure taskIds is an array
    const ids = Array.isArray(taskIds) ? taskIds : [taskIds];
    
    console.log('Bulk updating tasks:', ids, 'with updates:', updates);
    
    // Update each task individually
    for (const id of ids) {
      console.log('Updating task:', id);
      const { data, error } = await supabase
        .from('tasks')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select();

      if (error) {
        console.error('Error updating task:', id, error);
        throw error;
      }
      console.log('Update result:', data);
    }
    
    // Refetch to update UI
    await fetchDeal();
  };

  return {
    deal,
    tasks,
    loading,
    error,
    updateDeal,
    updateTask,
    addTask,
    deleteTask,
    uploadFile,
    deleteFile,
    applyDueDates,
    markTasksRequested,
    bulkUpdateTasks,
    refresh: fetchDeal,
    getFileUrl
  };
}

// Hook for team members
export function useTeam(organizationId) {
  const [team, setTeam] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const fetchTeam = async () => {
      try {
        // Fetch current team members + their auth last_sign_in_at via the
        // get_team_last_logins RPC (auth.users isn't directly readable
        // from the public client). Merge results into a single array so
        // the AdminView team list can render a 'Last login' chip per row.
        const [teamRes, loginsRes] = await Promise.all([
          supabase
            .from('users')
            .select('id, email, full_name, role, created_at')
            .eq('organization_id', organizationId)
            .order('full_name'),
          supabase.rpc('get_team_last_logins', { p_org_id: organizationId }),
        ]);

        if (!teamRes.error) {
          const loginMap = {};
          if (!loginsRes.error && Array.isArray(loginsRes.data)) {
            for (const r of loginsRes.data) loginMap[r.user_id] = r.last_sign_in_at;
          }
          const merged = (teamRes.data || []).map(m => ({
            ...m,
            last_sign_in_at: loginMap[m.id] || null,
          }));
          setTeam(merged);
        }

        // Fetch pending invites
        const { data: inviteData, error: inviteError } = await supabase
          .from('team_invites')
          .select('*')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false });

        if (!inviteError) setInvites(inviteData || []);
      } catch (err) {
        console.error('Error fetching team:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchTeam();

    // Subscribe to changes
    const teamChannel = supabase
      .channel('team-changes')
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'users', filter: `organization_id=eq.${organizationId}` },
        () => fetchTeam()
      )
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'team_invites', filter: `organization_id=eq.${organizationId}` },
        () => fetchTeam()
      )
      .subscribe();

    return () => {
      supabase.removeChannel(teamChannel);
    };
  }, [organizationId]);

  const inviteMember = async (email, fullName, role = 'member') => {
    // Check if already a team member
    const existingMember = team.find(m => m.email?.toLowerCase() === email.toLowerCase());
    if (existingMember) {
      throw new Error('This person is already a team member');
    }

    // Get current user for invited_by
    const { data: { user } } = await supabase.auth.getUser();
    
    // Get current user's name and org name for the email
    const { data: currentUserData } = await supabase
      .from('users')
      .select('full_name, organizations(name)')
      .eq('id', user?.id)
      .single();

    // Check for existing invite in database and delete it
    const { data: existingInvites } = await supabase
      .from('team_invites')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('email', email.toLowerCase());
    
    if (existingInvites && existingInvites.length > 0) {
      // Delete old invites for this email
      await supabase
        .from('team_invites')
        .delete()
        .eq('organization_id', organizationId)
        .eq('email', email.toLowerCase());
    }

    // Create the invite
    const { data, error } = await supabase
      .from('team_invites')
      .insert({
        organization_id: organizationId,
        email: email.toLowerCase(),
        full_name: fullName,
        role,
        invited_by: user?.id
      })
      .select()
      .single();

    if (error) {
      console.error('Invite error:', error);
      throw new Error(error.message || 'Failed to send invite');
    }

    // Send the invite email via Edge Function
    try {
      const emailResponse = await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-team-invite`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
          },
          body: JSON.stringify({
            invite_id: data.id,
            email: email.toLowerCase(),
            full_name: fullName,
            role,
            organization_name: currentUserData?.organizations?.name || 'Landmark Legacy Room',
            invited_by_name: currentUserData?.full_name || 'A team member'
          }),
        }
      );
      
      if (!emailResponse.ok) {
        console.warn('Failed to send invite email, but invite was created');
      }
    } catch (emailError) {
      console.warn('Failed to send invite email:', emailError);
      // Don't throw - invite was still created
    }

    // Update local state - remove old invites for this email and add new one
    setInvites(prev => [data, ...prev.filter(i => i.email?.toLowerCase() !== email.toLowerCase())]);
    return data;
  };

  const cancelInvite = async (inviteId) => {
    const { error } = await supabase
      .from('team_invites')
      .delete()
      .eq('id', inviteId);

    if (error) throw error;
    setInvites(prev => prev.filter(i => i.id !== inviteId));
  };

  // Re-sends the invite email for a still-pending invite — same edge
  // function as the original invite, just triggered again. Re-uses the
  // existing team_invites row (keeps pending_deal_id, created_at, etc.)
  // instead of deleting/recreating like inviteMember does for a brand new
  // invite, since send-team-invite already generates a fresh recovery link
  // and no-ops createUser if the auth user already exists.
  const resendInvite = async (invite) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data: currentUserData } = await supabase
      .from('users')
      .select('full_name, organizations(name)')
      .eq('id', user?.id)
      .single();

    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-team-invite`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({
          invite_id: invite.id,
          email: invite.email,
          full_name: invite.full_name,
          role: invite.role,
          organization_name: currentUserData?.organizations?.name || 'Landmark Legacy Room',
          invited_by_name: currentUserData?.full_name || 'A team member'
        }),
      }
    );

    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error || 'Failed to resend invite');
    }
  };

  const updateMemberRole = async (userId, role) => {
    const { error } = await supabase
      .from('users')
      .update({ role })
      .eq('id', userId);

    if (error) throw error;
    setTeam(prev => prev.map(m => m.id === userId ? { ...m, role } : m));
  };

  const removeMember = async (userId) => {
    // Check if this is the last admin (client-side check for UI)
    const member = team.find(m => m.id === userId);
    if (member?.role === 'admin') {
      const adminCount = team.filter(m => m.role === 'admin').length;
      if (adminCount <= 1) {
        throw new Error('Cannot remove the last admin. Promote another member to admin first.');
      }
    }
    
    // Use Edge Function with service role to bypass RLS
    const { data: { session } } = await supabase.auth.getSession();
    
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-actions`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`
        },
        body: JSON.stringify({
          action: 'remove_member',
          userId
        }),
      }
    );
    
    const result = await response.json();
    
    if (!response.ok) {
      throw new Error(result.error || 'Failed to remove member');
    }
    
    setTeam(prev => prev.filter(m => m.id !== userId));
  };

  const sendPasswordReset = async (email) => {
    // This calls an Edge Function that uses the service role to send password reset
    const response = await fetch(
      `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/admin-reset-password`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`
        },
        body: JSON.stringify({ email }),
      }
    );
    
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.message || 'Failed to send password reset');
    }
    
    return { success: true };
  };

  return {
    team,
    invites,
    loading,
    inviteMember,
    cancelInvite,
    resendInvite,
    updateMemberRole,
    removeMember,
    sendPasswordReset
  };
}

// Hook for organization settings
export function useOrganization(organizationId) {
  const [organization, setOrganization] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const fetchData = async () => {
      try {
        const [orgResult, templatesResult] = await Promise.all([
          supabase.from('organizations').select('*').eq('id', organizationId).single(),
          supabase.from('task_templates').select('*').eq('organization_id', organizationId).order('sort_order')
        ]);

        if (!orgResult.error) setOrganization(orgResult.data);
        if (!templatesResult.error) setTemplates(templatesResult.data || []);
      } catch (err) {
        console.error('Error fetching organization data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [organizationId]);

  const updateOrganization = async (updates) => {
    const { error } = await supabase
      .from('organizations')
      .update(updates)
      .eq('id', organizationId);

    if (error) throw error;
    setOrganization(prev => ({ ...prev, ...updates }));
  };

  const addTemplate = async (templateData) => {
    const { data, error } = await supabase
      .from('task_templates')
      .insert({
        organization_id: organizationId,
        template_id: `custom-${Date.now()}`,
        ...templateData
      })
      .select()
      .single();

    if (error) throw error;
    setTemplates(prev => [...prev, data]);
    return data;
  };

  const updateTemplate = async (templateId, updates) => {
    const { data, error } = await supabase
      .from('task_templates')
      .update(updates)
      .eq('id', templateId)
      .select()
      .single();

    if (error) throw error;
    setTemplates(prev => prev.map(t => t.id === templateId ? data : t));
    return data;
  };

  const deleteTemplate = async (templateId) => {
    const { error } = await supabase
      .from('task_templates')
      .delete()
      .eq('id', templateId);

    if (error) throw error;
    setTemplates(prev => prev.filter(t => t.id !== templateId));
  };

  return {
    organization,
    templates,
    loading,
    updateOrganization,
    addTemplate,
    updateTemplate,
    deleteTemplate
  };
}

// Hook for deal stages (deal-level statuses like Prospect, Diligence, etc.)
export function useDealStages(organizationId) {
  const [stages, setStages] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStages = useCallback(async () => {
    try {
      // Fetch org-specific stages, falling back to global defaults
      const { data, error } = await supabase
        .from('deal_stages')
        .select('*')
        .or(`organization_id.eq.${organizationId},organization_id.is.null`)
        .order('sort_order');

      if (error) throw error;
      
      // Prefer org-specific over global defaults
      const stageMap = new Map();
      data?.forEach(stage => {
        if (!stageMap.has(stage.id) || stage.organization_id === organizationId) {
          stageMap.set(stage.id, stage);
        }
      });
      
      setStages(Array.from(stageMap.values()).sort((a, b) => a.sort_order - b.sort_order));
    } catch (err) {
      console.error('Error fetching deal stages:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) fetchStages();
  }, [organizationId, fetchStages]);

  const addStage = async (stageData) => {
    const id = stageData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const { data, error } = await supabase
      .from('deal_stages')
      .insert({
        id: `${id}-${Date.now()}`,
        organization_id: organizationId,
        ...stageData,
        sort_order: stages.length + 1
      })
      .select()
      .single();

    if (error) throw error;
    setStages(prev => [...prev, data]);
    return data;
  };

  const updateStage = async (stageId, updates) => {
    const { data, error } = await supabase
      .from('deal_stages')
      .update(updates)
      .eq('id', stageId)
      .select()
      .single();

    if (error) throw error;
    setStages(prev => prev.map(s => s.id === stageId ? data : s));
    return data;
  };

  const deleteStage = async (stageId) => {
    const { error } = await supabase
      .from('deal_stages')
      .delete()
      .eq('id', stageId)
      .eq('organization_id', organizationId); // Can only delete org-specific stages

    if (error) throw error;
    setStages(prev => prev.filter(s => s.id !== stageId));
  };

  return { stages, loading, addStage, updateStage, deleteStage, refetch: fetchStages };
}

// Hook for task statuses (task-level statuses like Pending, Complete, etc.)
export function useTaskStatuses(organizationId) {
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStatuses = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('task_statuses')
        .select('*')
        .or(`organization_id.eq.${organizationId},organization_id.is.null`)
        .order('sort_order');

      if (error) throw error;
      
      // Prefer org-specific over global defaults
      const statusMap = new Map();
      data?.forEach(status => {
        if (!statusMap.has(status.id) || status.organization_id === organizationId) {
          statusMap.set(status.id, status);
        }
      });
      
      setStatuses(Array.from(statusMap.values()).sort((a, b) => a.sort_order - b.sort_order));
    } catch (err) {
      console.error('Error fetching task statuses:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) fetchStatuses();
  }, [organizationId, fetchStatuses]);

  const addStatus = async (statusData) => {
    const id = statusData.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
    const { data, error } = await supabase
      .from('task_statuses')
      .insert({
        id: `${id}-${Date.now()}`,
        organization_id: organizationId,
        ...statusData,
        sort_order: statuses.length + 1
      })
      .select()
      .single();

    if (error) throw error;
    setStatuses(prev => [...prev, data]);
    return data;
  };

  const updateStatus = async (statusId, updates) => {
    const { data, error } = await supabase
      .from('task_statuses')
      .update(updates)
      .eq('id', statusId)
      .select()
      .single();

    if (error) throw error;
    setStatuses(prev => prev.map(s => s.id === statusId ? data : s));
    return data;
  };

  const deleteStatus = async (statusId) => {
    const { error } = await supabase
      .from('task_statuses')
      .delete()
      .eq('id', statusId)
      .eq('organization_id', organizationId);

    if (error) throw error;
    setStatuses(prev => prev.filter(s => s.id !== statusId));
  };

  return { statuses, loading, addStatus, updateStatus, deleteStatus, refetch: fetchStatuses };
}

// Hook for audit log
export function useAuditLog(organizationId) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const fetchLogs = async () => {
      try {
        const { data, error } = await supabase
          .from('audit_log')
          .select('*')
          .eq('organization_id', organizationId)
          .order('created_at', { ascending: false })
          .limit(100);

        if (error) throw error;
        setLogs(data || []);
      } catch (err) {
        console.error('Error fetching audit logs:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [organizationId]);

  return { logs, loading };
}

// ============================================================================
// ACTIVITY FEED HOOK
// ============================================================================
export function useActivityFeed(organizationId, options = {}) {
  const { limit = 50, dealId = null } = options;
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hasMore, setHasMore] = useState(false);

  const fetchActivities = useCallback(async (offset = 0) => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      let query = supabase
        .from('activity_log')
        .select('*, users(full_name, email), deals(name)')
        .eq('organization_id', organizationId)
        .order('created_at', { ascending: false })
        .range(offset, offset + limit - 1);
      
      if (dealId) {
        query = query.eq('deal_id', dealId);
      }

      const { data, error } = await query;

      if (error) throw error;
      
      if (offset === 0) {
        setActivities(data || []);
      } else {
        setActivities(prev => [...prev, ...(data || [])]);
      }
      
      setHasMore((data?.length || 0) === limit);
    } catch (err) {
      console.error('Error fetching activity feed:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId, dealId, limit]);

  useEffect(() => {
    fetchActivities();
  }, [fetchActivities]);

  const loadMore = () => {
    fetchActivities(activities.length);
  };

  return { activities, loading, hasMore, loadMore, refresh: () => fetchActivities(0) };
}

// ============================================================================
// DEAL STAGE HISTORY HOOK
// ============================================================================
export function useDealStageHistory(dealId) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dealId) {
      setLoading(false);
      return;
    }

    const fetchHistory = async () => {
      try {
        const { data, error } = await supabase
          .from('deal_stage_history')
          .select('*, users:changed_by(full_name)')
          .eq('deal_id', dealId)
          .order('changed_at', { ascending: true });

        if (error) throw error;
        setHistory(data || []);
      } catch (err) {
        console.error('Error fetching stage history:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchHistory();
  }, [dealId]);

  return { history, loading };
}

// ============================================================================
// LFG LOCATIONS HOOK
// ============================================================================
export function useLfgLocations(organizationId) {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('lfg_locations')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name');

      if (error) throw error;
      setLocations(data || []);
    } catch (err) {
      console.error('Error fetching LFG locations:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchLocations();
  }, [fetchLocations]);

  const addLocation = async (locationData) => {
    const { data, error } = await supabase
      .from('lfg_locations')
      .insert({
        organization_id: organizationId,
        ...locationData
      })
      .select()
      .single();

    if (error) throw error;
    setLocations(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
    return data;
  };

  const updateLocation = async (locationId, updates) => {
    const { data, error } = await supabase
      .from('lfg_locations')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', locationId)
      .select()
      .single();

    if (error) throw error;
    setLocations(prev => prev.map(l => l.id === locationId ? data : l));
    return data;
  };

  const deleteLocation = async (locationId) => {
    const { error } = await supabase
      .from('lfg_locations')
      .update({ is_active: false })
      .eq('id', locationId);

    if (error) throw error;
    setLocations(prev => prev.filter(l => l.id !== locationId));
  };

  return {
    locations,
    loading,
    addLocation,
    updateLocation,
    deleteLocation,
    geocodeAddress,  // re-exported from supabase.js for backward compat
    refresh: fetchLocations
  };
}

// ============================================================================
// DIGEST SETTINGS HOOK
// ============================================================================
export function useDigestSettings(organizationId) {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) {
      setLoading(false);
      return;
    }

    const fetchSettings = async () => {
      try {
        const { data, error } = await supabase
          .from('digest_settings')
          .select('*')
          .eq('organization_id', organizationId)
          .single();

        if (error && error.code !== 'PGRST116') throw error;
        setSettings(data || { is_enabled: false, recipient_user_ids: [] });
      } catch (err) {
        console.error('Error fetching digest settings:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchSettings();
  }, [organizationId]);

  const updateSettings = async (updates) => {
    try {
      const { data, error } = await supabase
        .from('digest_settings')
        .upsert({
          organization_id: organizationId,
          ...settings,
          ...updates,
          updated_at: new Date().toISOString()
        }, { onConflict: 'organization_id' })
        .select()
        .single();

      if (error) throw error;
      setSettings(data);
      return data;
    } catch (err) {
      console.error('Error updating digest settings:', err);
      throw err;
    }
  };

  return { settings, loading, updateSettings };
}
// Hook for managing deal locations (individual locations within a deal).
// Phase C.6 — AI-proposed locations (web search or document extraction)
// land as is_confirmed=false so they never silently affect the map or any
// external-facing export; `locations` stays exactly what every existing
// consumer already expects (confirmed only, same shape as before this
// column existed), while `pendingLocations` is new and only consumed by
// the confirm/reject UI in DealView.
export function useDealLocations(dealId, organizationId) {
  const [allLocations, setAllLocations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchLocations = useCallback(async () => {
    if (!dealId) {
      setLoading(false);
      return;
    }
    try {
      const { data, error } = await supabase
        .from('deal_locations')
        .select('*')
        .eq('deal_id', dealId)
        .order('name');

      if (error) throw error;
      setAllLocations((data || []).map(titleCaseLocation));
    } catch (err) {
      console.error('Error fetching deal locations:', err);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchLocations();
    if (!dealId) return;
    // Pending locations can land while the deal is open (async document
    // processing, a re-run of ai-enrich-deal) — without this the user
    // wouldn't see the confirm banner appear until they left and came back.
    const channel = supabase
      .channel(`deal-locations-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'deal_locations', filter: `deal_id=eq.${dealId}` },
        () => fetchLocations()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [dealId, fetchLocations]);

  const locations = allLocations.filter(l => l.is_confirmed !== false);
  const pendingLocations = allLocations.filter(l => l.is_confirmed === false);

  const addLocation = async (locationData) => {
    try {
      const { data, error } = await supabase
        .from('deal_locations')
        .insert({
          deal_id: dealId,
          organization_id: organizationId,
          ...locationData
        })
        .select()
        .single();

      if (error) throw error;
      setAllLocations(prev => [...prev, data].sort((a, b) => a.name.localeCompare(b.name)));
      return data;
    } catch (err) {
      console.error('Error adding deal location:', err);
      throw err;
    }
  };

  const updateLocation = async (locationId, updates) => {
    try {
      const { data, error } = await supabase
        .from('deal_locations')
        .update({ ...updates, updated_at: new Date().toISOString() })
        .eq('id', locationId)
        .select()
        .single();

      if (error) throw error;
      const titleCased = titleCaseLocation(data);
      setAllLocations(prev => prev.map(l => l.id === locationId ? titleCased : l));
      return titleCased;
    } catch (err) {
      console.error('Error updating deal location:', err);
      throw err;
    }
  };

  const deleteLocation = async (locationId) => {
    try {
      const { error } = await supabase
        .from('deal_locations')
        .delete()
        .eq('id', locationId);

      if (error) throw error;
      setAllLocations(prev => prev.filter(l => l.id !== locationId));
    } catch (err) {
      console.error('Error deleting deal location:', err);
      throw err;
    }
  };

  // Confirms an AI-proposed location — records who confirmed it, and it
  // starts showing on the map / in exports from this point on.
  const confirmLocation = async (locationId, confirmedByUser) => {
    try {
      const { data, error } = await supabase
        .from('deal_locations')
        .update({
          is_confirmed: true,
          confirmed_by: confirmedByUser?.id || null,
          confirmed_by_name: confirmedByUser?.full_name || confirmedByUser?.email || null,
          confirmed_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        })
        .eq('id', locationId)
        .select()
        .single();

      if (error) throw error;
      const titleCased = titleCaseLocation(data);
      setAllLocations(prev => prev.map(l => l.id === locationId ? titleCased : l));
      return titleCased;
    } catch (err) {
      console.error('Error confirming deal location:', err);
      throw err;
    }
  };

  // Rejects an AI-proposed location — it was never real, so the row is
  // removed outright rather than kept in a rejected state.
  const rejectLocation = async (locationId) => {
    try {
      const { error } = await supabase
        .from('deal_locations')
        .delete()
        .eq('id', locationId);

      if (error) throw error;
      setAllLocations(prev => prev.filter(l => l.id !== locationId));
    } catch (err) {
      console.error('Error rejecting deal location:', err);
      throw err;
    }
  };

  return {
    locations,
    pendingLocations,
    loading,
    addLocation,
    updateLocation,
    deleteLocation,
    confirmLocation,
    rejectLocation,
    refresh: fetchLocations,
    geocodeAddress  // re-exported from supabase.js
  };
}


// Hook for managing deal notes
export function useDealNotes(dealId, organizationId) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dealId) {
      setLoading(false);
      return;
    }

    const fetchNotes = async () => {
      try {
        const { data, error } = await supabase
          .from('deal_notes')
          .select('*')
          .eq('deal_id', dealId)
          .order('created_at', { ascending: false });

        if (error) throw error;
        setNotes(data || []);
      } catch (err) {
        console.error('Error fetching deal notes:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotes();
  }, [dealId]);

  const addNote = async (content, userName, userId) => {
    try {
      const { data, error } = await supabase
        .from('deal_notes')
        .insert({
          deal_id: dealId,
          organization_id: organizationId,
          user_id: userId,
          user_name: userName,
          content: content.trim()
        })
        .select()
        .single();

      if (error) throw error;
      setNotes(prev => [data, ...prev]);
      return data;
    } catch (err) {
      console.error('Error adding note:', err);
      throw err;
    }
  };

  const updateNote = async (noteId, content) => {
    try {
      const { data, error } = await supabase
        .from('deal_notes')
        .update({ content: content.trim(), updated_at: new Date().toISOString() })
        .eq('id', noteId)
        .select()
        .single();

      if (error) throw error;
      setNotes(prev => prev.map(n => n.id === noteId ? data : n));
      return data;
    } catch (err) {
      console.error('Error updating note:', err);
      throw err;
    }
  };

  const deleteNote = async (noteId) => {
    try {
      const { error } = await supabase
        .from('deal_notes')
        .delete()
        .eq('id', noteId);

      if (error) throw error;
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (err) {
      console.error('Error deleting note:', err);
      throw err;
    }
  };

  return { notes, loading, addNote, updateNote, deleteNote };
}

export function useDealReminders(dealId) {
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dealId) return;
    fetchReminders();
    const sub = supabase
      .channel(`deal_reminders_${dealId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'deal_reminders', filter: `deal_id=eq.${dealId}` }, fetchReminders)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [dealId]);

  const fetchReminders = async () => {
    try {
      const { data, error } = await supabase
        .from('deal_reminders').select('*').eq('deal_id', dealId).order('due_date', { ascending: true });
      if (error) throw error;
      setReminders(data || []);
    } catch (err) { console.error('Error fetching reminders:', err); }
    finally { setLoading(false); }
  };

  const addReminder = async (fields) => {
    const { error } = await supabase.from('deal_reminders').insert({ deal_id: dealId, status: 'pending', ...fields });
    if (error) throw error;
    await fetchReminders();
  };

  const updateReminder = async (id, fields) => {
    const { error } = await supabase.from('deal_reminders').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    await fetchReminders();
  };

  const completeReminder = async (id) => {
    const { error } = await supabase.from('deal_reminders').update({ status: 'completed', updated_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    await fetchReminders();
  };

  const dismissReminder = async (id) => {
    const { error } = await supabase.from('deal_reminders').update({ status: 'dismissed', updated_at: new Date().toISOString() }).eq('id', id);
    if (error) throw error;
    await fetchReminders();
  };

  const deleteReminder = async (id) => {
    const { error } = await supabase.from('deal_reminders').delete().eq('id', id);
    if (error) throw error;
    await fetchReminders();
  };

  return { reminders, loading, addReminder, updateReminder, completeReminder, dismissReminder, deleteReminder };
}

export function useVendors(organizationId) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) return;
    fetchVendors();
    const sub = supabase
      .channel(`vendors_${organizationId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vendors', filter: `organization_id=eq.${organizationId}` }, fetchVendors)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [organizationId]);

  const fetchVendors = async () => {
    try {
      const { data, error } = await supabase
        .from('vendors')
        .select('*, vendor_categories(name)')
        .eq('organization_id', organizationId)
        .order('name', { ascending: true });
      if (error) throw error;
      setVendors(data || []);
    } catch (err) { console.error('Error fetching vendors:', err); }
    finally { setLoading(false); }
  };

  const createVendor = async (fields) => {
    const { data, error } = await supabase
      .from('vendors')
      .insert({ organization_id: organizationId, ...fields })
      .select('*, vendor_categories(name)')
      .single();
    if (error) throw error;
    await fetchVendors();
    return data;
  };

  const deleteVendor = async (id) => {
    const { error } = await supabase.from('vendors').delete().eq('id', id);
    if (error) throw error;
    await fetchVendors();
  };

  return { vendors, loading, createVendor, deleteVendor };
}

export function useVendorCategories(organizationId) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) return;
    fetchCategories();
  }, [organizationId]);

  const fetchCategories = async () => {
    try {
      const { data, error } = await supabase
        .from('vendor_categories')
        .select('*')
        .eq('organization_id', organizationId)
        .order('name', { ascending: true });
      if (error) throw error;
      setCategories(data || []);
    } catch (err) { console.error('Error fetching vendor categories:', err); }
    finally { setLoading(false); }
  };

  const createCategory = async (name) => {
    const { data, error } = await supabase
      .from('vendor_categories')
      .insert({ organization_id: organizationId, name })
      .select().single();
    if (error) throw error;
    await fetchCategories();
    return data;
  };

  return { categories, loading, createCategory, refetch: fetchCategories };
}

export function useVendor(vendorId) {
  const [vendor, setVendor] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!vendorId) return;
    fetchAll();
    const sub = supabase
      .channel(`vendor_${vendorId}`)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vendors', filter: `id=eq.${vendorId}` }, fetchAll)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vendor_contacts', filter: `vendor_id=eq.${vendorId}` }, fetchContacts)
      .on('postgres_changes', { event: '*', schema: 'public', table: 'vendor_notes', filter: `vendor_id=eq.${vendorId}` }, fetchNotes)
      .subscribe();
    return () => supabase.removeChannel(sub);
  }, [vendorId]);

  const fetchAll = async () => {
    await Promise.all([fetchVendor(), fetchContacts(), fetchNotes()]);
    setLoading(false);
  };

  const fetchVendor = async () => {
    const { data, error } = await supabase
      .from('vendors').select('*, vendor_categories(name)').eq('id', vendorId).single();
    if (error) { console.error('Error fetching vendor:', error); return; }
    setVendor(data);
  };

  const fetchContacts = async () => {
    const { data, error } = await supabase
      .from('vendor_contacts').select('*').eq('vendor_id', vendorId).order('is_primary', { ascending: false });
    if (!error) setContacts(data || []);
  };

  const fetchNotes = async () => {
    const { data, error } = await supabase
      .from('vendor_notes').select('*').eq('vendor_id', vendorId).order('created_at', { ascending: false });
    if (!error) setNotes(data || []);
  };

  const updateVendor = async (fields) => {
    const { error } = await supabase
      .from('vendors').update({ ...fields, updated_at: new Date().toISOString() }).eq('id', vendorId);
    if (error) throw error;
    await fetchVendor();
  };

  const addContact = async (fields) => {
    const { error } = await supabase
      .from('vendor_contacts').insert({ vendor_id: vendorId, ...fields });
    if (error) throw error;
    await fetchContacts();
  };

  const deleteContact = async (id) => {
    const { error } = await supabase.from('vendor_contacts').delete().eq('id', id);
    if (error) throw error;
    await fetchContacts();
  };

  const addNote = async (content, authorName) => {
    const { error } = await supabase
      .from('vendor_notes').insert({ vendor_id: vendorId, content, author_name: authorName });
    if (error) throw error;
    await fetchNotes();
  };

  const deleteNote = async (id) => {
    const { error } = await supabase.from('vendor_notes').delete().eq('id', id);
    if (error) throw error;
    await fetchNotes();
  };

  return { vendor, contacts, notes, loading, updateVendor, addContact, deleteContact, addNote, deleteNote };
}

export function useCapitalForecast(organizationId) {
  const [forecast, setForecast] = useState([]);
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!organizationId) return;
    fetchAll();
  }, [organizationId]);

  const fetchAll = async () => {
    try {
      // Deals with financial fields for pipeline/closing forecast
      const { data: deals, error: dealsErr } = await supabase
        .from('deals')
        .select('id, name, stage, target_close_date, purchase_price, ebitda, annual_revenue')
        .eq('organization_id', organizationId)
        .is('deleted_at', null)
        .order('target_close_date', { ascending: true });
      if (dealsErr) throw dealsErr;

      // All deal reminders with amounts (financial obligations)
      const { data: rems, error: remsErr } = await supabase
        .from('deal_reminders')
        .select('*, deals(name)')
        .eq('organization_id', organizationId)
        .eq('status', 'pending')
        .not('amount', 'is', null)
        .order('due_date', { ascending: true });
      if (remsErr) throw remsErr;

      setForecast(deals || []);
      setReminders(rems || []);
    } catch (err) {
      console.error('Error fetching capital forecast:', err);
    } finally {
      setLoading(false);
    }
  };

  return { forecast, reminders, loading, refetch: fetchAll };
}

// ============================================================================
// DEAL OBLIGATIONS HOOK (Liens & Contingencies)
// ============================================================================
export function useDealObligations(dealId, organizationId) {
  const [obligations, setObligations] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchObligations = useCallback(async () => {
    if (!dealId) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('deal_obligations')
        .select('*')
        .eq('deal_id', dealId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      setObligations(data || []);
    } catch (err) {
      console.error('Error fetching obligations:', err);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchObligations();
    if (!dealId) return;
    const channel = supabase
      .channel(`obligations-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'deal_obligations', filter: `deal_id=eq.${dealId}` },
        () => fetchObligations()
      )
      .subscribe();
    return () => supabase.removeChannel(channel);
  }, [dealId, fetchObligations]);

  const addObligation = async (data) => {
    const { error } = await supabase
      .from('deal_obligations')
      .insert({ deal_id: dealId, organization_id: organizationId, ...data });
    if (error) throw error;
    await fetchObligations();
  };

  const updateObligation = async (id, updates) => {
    const { error } = await supabase
      .from('deal_obligations')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', id);
    if (error) throw error;
    await fetchObligations();
  };

  const deleteObligation = async (id) => {
    const { error } = await supabase
      .from('deal_obligations')
      .delete()
      .eq('id', id);
    if (error) throw error;
    await fetchObligations();
  };

  return { obligations, loading, addObligation, updateObligation, deleteObligation };
}

// Hook for deal-scoped access management
export function useDealAccess(dealId) {
  const [accessList, setAccessList] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchAccess = useCallback(async () => {
    if (!dealId) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('deal_access')
        .select('*, user:users!user_id(id, email, full_name, role)')
        .eq('deal_id', dealId);
      if (error) throw error;
      setAccessList(data || []);
    } catch (err) {
      console.error('Error fetching deal access:', err);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => { fetchAccess(); }, [fetchAccess]);

  const grantAccess = async (userId) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { error } = await supabase
      .from('deal_access')
      .insert({ deal_id: dealId, user_id: userId, granted_by: user?.id });
    if (error) throw error;
    await fetchAccess();
  };

  const revokeAccess = async (accessId) => {
    const { error } = await supabase
      .from('deal_access')
      .delete()
      .eq('id', accessId);
    if (error) throw error;
    await fetchAccess();
  };

  // Grant by email — covers three cases:
  //   a) email matches existing user → insert deal_access immediately
  //   b) email already has a pending team_invite → re-stamp it with the
  //      pending_deal_id so the existing invite link still works
  //   c) brand-new email → create a deal_advisor invite scoped to this
  //      deal, send the standard team-invite email
  // Returns { kind: 'granted' | 'invited' | 'noop', message }.
  const grantAccessByEmail = async (email, fullName, organizationId) => {
    const e = (email || '').trim().toLowerCase();
    if (!e || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e)) throw new Error('Enter a valid email');
    const { data: { user } } = await supabase.auth.getUser();

    // (a) Existing user?
    const { data: existing } = await supabase
      .from('users').select('id, organization_id, role').eq('email', e).maybeSingle();
    if (existing?.id) {
      // Already on the org? Just create the deal_access row (idempotent).
      const already = accessList.some(a => a.user_id === existing.id);
      if (already) return { kind: 'noop', message: 'Already has access' };
      const { error } = await supabase
        .from('deal_access')
        .insert({ deal_id: dealId, user_id: existing.id, granted_by: user?.id });
      if (error) throw error;
      await fetchAccess();

      // Notify — they have an account already, but have no other way to
      // know this deal was just added. Reuses send-deal-advisor-request's
      // existing fresh-magic-link + branded-email plumbing rather than
      // duplicating it. Best-effort: grant already succeeded above either way.
      try {
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-deal-advisor-request`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            deal_id: dealId,
            advisor_user_id: existing.id,
            subject: "You've been granted access to a deal",
            message: "You now have access to this deal. Click below to open it.",
          }),
        });
      } catch (e2) {
        console.warn('grant-access notification email failed (access still granted):', e2);
      }

      return { kind: 'granted', message: 'Access granted' };
    }

    // (b) Existing pending invite?
    const { data: existingInvite } = await supabase
      .from('team_invites')
      .select('id')
      .eq('organization_id', organizationId)
      .eq('email', e)
      .is('accepted_at', null)
      .maybeSingle();
    if (existingInvite?.id) {
      const { error } = await supabase
        .from('team_invites')
        .update({ pending_deal_id: dealId, role: 'deal_advisor', updated_at: new Date().toISOString() })
        .eq('id', existingInvite.id);
      if (error) throw error;

      // They haven't accepted their original invite yet — re-send it so
      // they actually learn a deal was added (otherwise this update is
      // invisible until/unless they happen to click the old link).
      // send-team-invite already treats "auth user already exists" as a
      // harmless no-op and still issues a fresh magic link either way.
      try {
        const { data: meData } = await supabase
          .from('users').select('full_name, organizations(name)').eq('id', user?.id).single();
        await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-team-invite`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
          },
          body: JSON.stringify({
            invite_id: existingInvite.id,
            email: e,
            full_name: fullName || '',
            role: 'deal_advisor',
            organization_name: meData?.organizations?.name || 'Landmark Legacy Room',
            invited_by_name: meData?.full_name || 'A team member',
          }),
        });
      } catch (e2) {
        console.warn('re-invite email send failed (invite still updated):', e2);
      }

      return { kind: 'invited', message: 'Updated existing invite to grant deal access on signup' };
    }

    // (c) Fresh invite
    const { data: invite, error: inviteErr } = await supabase
      .from('team_invites')
      .insert({
        organization_id: organizationId,
        email: e,
        full_name: fullName || null,
        role: 'deal_advisor',
        invited_by: user?.id,
        pending_deal_id: dealId,
      })
      .select().single();
    if (inviteErr) throw inviteErr;

    // Fire the email — best effort
    try {
      const { data: meData } = await supabase
        .from('users').select('full_name, organizations(name)').eq('id', user?.id).single();
      await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/send-team-invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
        body: JSON.stringify({
          invite_id: invite.id,
          email: e,
          full_name: fullName || '',
          role: 'deal_advisor',
          organization_name: meData?.organizations?.name || 'Landmark Legacy Room',
          invited_by_name: meData?.full_name || 'A team member',
        }),
      });
    } catch (e2) {
      console.warn('invite email send failed (invite still created):', e2);
    }

    return { kind: 'invited', message: 'Invite sent — they\'ll get deal access on signup' };
  };

  return { accessList, loading, grantAccess, grantAccessByEmail, revokeAccess, refresh: fetchAccess };
}
// ============================================================================
// DUE DILIGENCE CHECKLIST HOOKS (Phase A/B)
// ============================================================================
// Powers the DiligenceSection component on DealView. Additive — no existing
// exports above are touched.

// List all active DD checklist templates (admin-managed, cross-org readable).
// Used by the seed-templates modal on DealView's Diligence section.
export function useDdTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTemplates = useCallback(async () => {
    try {
      const { data, error } = await supabase
        .from('dd_checklist_templates')
        .select('id, name, role, description, version, is_active')
        .eq('is_active', true)
        .order('role', { ascending: true });
      if (error) throw error;
      // Fetch counts per template in a second pass (small N, so fine)
      if (data && data.length > 0) {
        const { data: items, error: itemsErr } = await supabase
          .from('dd_checklist_template_items')
          .select('template_id');
        if (itemsErr) throw itemsErr;
        const counts = (items || []).reduce((acc, r) => {
          acc[r.template_id] = (acc[r.template_id] || 0) + 1;
          return acc;
        }, {});
        setTemplates(data.map(t => ({ ...t, item_count: counts[t.id] || 0 })));
      } else {
        setTemplates([]);
      }
    } catch (err) {
      console.error('Error fetching DD templates:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchTemplates(); }, [fetchTemplates]);

  return { templates, loading, refresh: fetchTemplates };
}

// Phase C.2 — deal milestone timeline. One row per milestone (12 by default),
// realtime-subscribed. Writes go through updateMilestone() which takes a patch
// of { target_date, actual_date, notes }.
export function useDealMilestones(dealId) {
  const [milestones, setMilestones] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchMilestones = useCallback(async () => {
    if (!dealId) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('deal_milestones')
        .select('*')
        .eq('deal_id', dealId)
        .order('sort_order', { ascending: true });
      if (error) throw error;
      // Auto-seed if missing (e.g. newly created deal before edge function seeds)
      if (!data || data.length === 0) {
        await supabase.rpc('seed_deal_milestones', { p_deal_id: dealId });
        const { data: retry } = await supabase
          .from('deal_milestones')
          .select('*')
          .eq('deal_id', dealId)
          .order('sort_order', { ascending: true });
        setMilestones(retry || []);
      } else {
        setMilestones(data);
      }
    } catch (err) {
      console.error('Error fetching milestones:', err);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchMilestones();
    if (!dealId) return;
    const channel = supabase
      .channel(`milestones-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'deal_milestones', filter: `deal_id=eq.${dealId}` },
        () => fetchMilestones()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [dealId, fetchMilestones]);

  const updateMilestone = async (id, patch) => {
    const { error } = await supabase
      .from('deal_milestones')
      .update(patch)
      .eq('id', id);
    if (error) throw error;
    await fetchMilestones();
  };

  return { milestones, loading, updateMilestone, refresh: fetchMilestones };
}

// Per-deal DD items with attached files. Realtime-subscribed.
// Returns both the flat item list and a precomputed grouping for rendering.
export function useDealDdItems(dealId, organizationId) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = useCallback(async () => {
    if (!dealId) { setLoading(false); return; }
    try {
      // Items, with files linked via dd_item_id. Supabase nested select.
      const { data, error } = await supabase
        .from('deal_dd_items')
        .select(`
          *,
          files:files!dd_item_id(
            id, original_name, storage_path, size_bytes, created_at,
            uploaded_by_name, uploaded_by_type, dd_match_confidence, sharepoint_url
          )
        `)
        .eq('deal_id', dealId)
        .order('section_sort', { ascending: true })
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setItems(data || []);
    } catch (err) {
      console.error('Error fetching DD items:', err);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchItems();
    if (!dealId) return;
    // Watch the items table for this deal. We also want to react to file
    // changes for files linked to this deal's items, but the 'files' table
    // filter is by deal_id, so we can piggyback on that.
    const itemsChannel = supabase
      .channel(`dd-items-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'deal_dd_items', filter: `deal_id=eq.${dealId}` },
        () => fetchItems()
      )
      .subscribe();
    const filesChannel = supabase
      .channel(`dd-items-files-${dealId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'files', filter: `deal_id=eq.${dealId}` },
        () => fetchItems()
      )
      .subscribe();
    return () => {
      supabase.removeChannel(itemsChannel);
      supabase.removeChannel(filesChannel);
    };
  }, [dealId, fetchItems]);

  // Seed items from one or more templates. Only creates items that don't
  // already exist (keyed by template_item_id) so re-seeding is idempotent.
  const seedFromTemplates = async (templateIds) => {
    if (!dealId || !organizationId) throw new Error('Missing dealId or organizationId');
    if (!templateIds || templateIds.length === 0) return { inserted: 0 };

    // 1. Load all template items for the chosen templates
    const { data: tmplItems, error: tErr } = await supabase
      .from('dd_checklist_template_items')
      .select('*')
      .in('template_id', templateIds);
    if (tErr) throw tErr;
    if (!tmplItems || tmplItems.length === 0) return { inserted: 0 };

    // 2. Find existing deal items already linked to any of these template items
    const { data: existing, error: eErr } = await supabase
      .from('deal_dd_items')
      .select('template_item_id')
      .eq('deal_id', dealId)
      .in('template_item_id', tmplItems.map(i => i.id));
    if (eErr) throw eErr;
    const existingSet = new Set((existing || []).map(r => r.template_item_id));

    // 3. Build inserts for items that don't already exist on this deal
    const toInsert = tmplItems
      .filter(ti => !existingSet.has(ti.id))
      .map(ti => ({
        deal_id: dealId,
        organization_id: organizationId,
        template_id: ti.template_id,
        template_item_id: ti.id,
        section: ti.section,
        section_sort: ti.section_sort,
        item_number: ti.item_number,
        request_text: ti.request_text,
        instructions: ti.instructions || null,
        priority: ti.priority,
        assigned_to: ti.default_assigned_to,
        qb_extractable: ti.qb_extractable,
        keywords: ti.keywords || [],
        sort_order: ti.sort_order,
      }));

    if (toInsert.length === 0) return { inserted: 0, skipped: tmplItems.length };

    const { error: iErr } = await supabase.from('deal_dd_items').insert(toInsert);
    if (iErr) throw iErr;
    await fetchItems();
    return { inserted: toInsert.length, skipped: tmplItems.length - toInsert.length };
  };

  const updateItem = async (id, patch) => {
    // Stamp received_at / completed_at when transitioning status
    const stamped = { ...patch };
    if (patch.status === 'received' && !patch.received_at) stamped.received_at = new Date().toISOString();
    if (patch.status === 'complete' && !patch.completed_at) stamped.completed_at = new Date().toISOString();
    if (patch.status === 'requested' && !patch.requested_at) stamped.requested_at = new Date().toISOString();
    const { error } = await supabase
      .from('deal_dd_items')
      .update(stamped)
      .eq('id', id);
    if (error) throw error;
    await fetchItems();
  };

  const deleteItem = async (id) => {
    const { error } = await supabase
      .from('deal_dd_items')
      .delete()
      .eq('id', id);
    if (error) throw error;
    await fetchItems();
  };

  const addCustomItem = async (item) => {
    const { error } = await supabase
      .from('deal_dd_items')
      .insert({
        deal_id: dealId,
        organization_id: organizationId,
        section: item.section || 'Custom',
        section_sort: item.section_sort ?? 999,
        item_number: item.item_number || 'custom',
        request_text: item.request_text,
        priority: item.priority || 'medium',
        assigned_to: item.assigned_to || 'seller',
        keywords: item.keywords || [],
        sort_order: item.sort_order ?? 999,
      });
    if (error) throw error;
    await fetchItems();
  };

  // Upload a file directly against a DD item from an authenticated session
  // (admin/member/deal_advisor) — the counterpart to the seller portal's
  // token-based record_seller_dd_upload, for the same underlying files
  // table (dd_item_id-linked). RLS already permits any org member
  // (deal_advisor included) to insert into files as long as
  // organization_id matches their own, so this is a plain insert, not an
  // RPC. Auto-advances not_started/requested items to received, matching
  // the seller-side behavior.
  const uploadDdItemFile = async (itemId, file, uploadedBy) => {
    const fileData = await uploadFileToStorage(file, dealId, `dd-${itemId}`, organizationId);
    const { data, error } = await supabase
      .from('files')
      .insert({
        dd_item_id: itemId,
        deal_id: dealId,
        organization_id: organizationId,
        name: fileData.name,
        original_name: fileData.originalName,
        storage_path: fileData.path,
        size_bytes: fileData.size,
        mime_type: fileData.mimeType,
        uploaded_by_id: uploadedBy?.id || null,
        uploaded_by_name: uploadedBy?.name || 'Unknown',
        uploaded_by_type: uploadedBy?.type || 'user',
      })
      .select()
      .single();
    if (error) throw error;

    const item = items.find(i => i.id === itemId);
    if (item && (item.status === 'not_started' || item.status === 'requested')) {
      await updateItem(itemId, { status: 'received' });
    } else {
      await fetchItems();
    }

    return data;
  };

  return {
    items,
    loading,
    seedFromTemplates,
    updateItem,
    deleteItem,
    addCustomItem,
    uploadDdItemFile,
    refresh: fetchItems,
  };
}

// Phase C.5.2 — Named territories for pipeline filtering. CRUD hook with
// realtime subscription. Deal-to-territory membership is computed on the
// client by unioning deal_locations.state against territory.states (and
// county_fips when we populate that column later).
export function useTerritories(organizationId) {
  const [territories, setTerritories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchTerritories = useCallback(async () => {
    if (!organizationId) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('territories')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('name', { ascending: true });
      if (error) throw error;
      setTerritories(data || []);
    } catch (err) {
      console.error('Error fetching territories:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchTerritories();
    if (!organizationId) return;
    const channel = supabase
      .channel(`territories-${organizationId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'territories', filter: `organization_id=eq.${organizationId}` },
        () => fetchTerritories()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [organizationId, fetchTerritories]);

  const createTerritory = async ({ name, description, color, states, county_fips, drawn_geometry }) => {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('territories')
      .insert({
        organization_id: organizationId,
        name: name.trim(),
        description: description || null,
        color: color || '#1A3665',
        states: states || [],
        county_fips: county_fips || [],
        drawn_geometry: drawn_geometry || null,
        created_by: user?.id || null,
      })
      .select()
      .single();
    if (error) throw error;
    await fetchTerritories();
    return data;
  };

  const updateTerritory = async (id, patch) => {
    const { error } = await supabase.from('territories').update(patch).eq('id', id);
    if (error) throw error;
    await fetchTerritories();
  };

  const deleteTerritory = async (id) => {
    // Soft-delete via is_active flag so downstream filters can ignore it
    // without losing history.
    const { error } = await supabase.from('territories').update({ is_active: false }).eq('id', id);
    if (error) throw error;
    await fetchTerritories();
  };

  return { territories, loading, createTerritory, updateTerritory, deleteTerritory, refresh: fetchTerritories };
}

// Portfolio taxonomy rework — admin-editable category lists behind the
// cancellation-reason and delay-reason dropdowns on a deal. One table,
// two `kind`s, fetched together since both are small and org-scoped.
export function useDealReasonCategories(organizationId) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchCategories = useCallback(async () => {
    if (!organizationId) { setLoading(false); return; }
    try {
      const { data, error } = await supabase
        .from('deal_reason_categories')
        .select('*')
        .eq('organization_id', organizationId)
        .eq('is_active', true)
        .order('kind', { ascending: true })
        .order('sort_order', { ascending: true });
      if (error) throw error;
      setCategories(data || []);
    } catch (err) {
      console.error('Error fetching deal reason categories:', err);
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => {
    fetchCategories();
    if (!organizationId) return;
    const channel = supabase
      .channel(`deal-reason-categories-${organizationId}`)
      .on('postgres_changes',
        { event: '*', schema: 'public', table: 'deal_reason_categories', filter: `organization_id=eq.${organizationId}` },
        () => fetchCategories()
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [organizationId, fetchCategories]);

  const cancellationCategories = categories.filter(c => c.kind === 'cancellation');
  const delayCategories = categories.filter(c => c.kind === 'delay');

  const addCategory = async (kind, label) => {
    const { data, error } = await supabase
      .from('deal_reason_categories')
      .insert({
        organization_id: organizationId,
        kind,
        label: label.trim(),
        sort_order: categories.filter(c => c.kind === kind).length,
      })
      .select()
      .single();
    if (error) throw error;
    await fetchCategories();
    return data;
  };

  const deactivateCategory = async (id) => {
    // Soft-delete via is_active so a deal that already used this category
    // keeps rendering its label via the FK, just no longer offered as an
    // option for new selections.
    const { error } = await supabase.from('deal_reason_categories').update({ is_active: false }).eq('id', id);
    if (error) throw error;
    await fetchCategories();
  };

  return { categories, cancellationCategories, delayCategories, loading, addCategory, deactivateCategory, refresh: fetchCategories };
}
